import { useEffect, useMemo, useState, useCallback } from "react";
import API_BASE_URL from "../config/api";
import "./PreMidtermAnalysis.css";
import "./KisiselPlanlama.css";

function pctColor(pct) {
  if (pct === null || pct === undefined) return "#e5e7eb";
  const v = Math.max(0, Math.min(100, pct));
  const hue = (v / 100) * 120;
  return `hsl(${hue}, 70%, 55%)`;
}

function PctCell({ value }) {
  const display = value === null || value === undefined ? "—" : value.toFixed(1);
  return (
    <span className="pm-pct-pill" style={{ background: pctColor(value) }}>
      {display}
    </span>
  );
}

function priorityLabel(pct) {
  if (pct === null || pct === undefined) return { text: "Veri Yok", cls: "kp-badge-gray" };
  if (pct < 40) return { text: "Kritik", cls: "kp-badge-red" };
  if (pct < 60) return { text: "Geliştirilmeli", cls: "kp-badge-orange" };
  if (pct < 75) return { text: "İyi Yönde", cls: "kp-badge-yellow" };
  return { text: "Yeterli", cls: "kp-badge-green" };
}

function buildSearchUrl(platform, searchQuery) {
  const q = encodeURIComponent(searchQuery || "");
  const p = (platform || "").toLowerCase();
  if (p.includes("coursera"))     return `https://www.coursera.org/search?query=${q}`;
  if (p.includes("udemy"))        return `https://www.udemy.com/courses/search/?q=${q}`;
  if (p.includes("youtube"))      return `https://www.youtube.com/results?search_query=${q}`;
  if (p.includes("edx"))          return `https://www.edx.org/search?q=${q}`;
  if (p.includes("khan"))         return `https://www.khanacademy.org/search?page_search_query=${q}`;
  if (p.includes("mit"))          return `https://ocw.mit.edu/search/?q=${q}`;
  if (p.includes("bilkent") || p.includes("itü") || p.includes("odtü"))
    return `https://www.google.com/search?q=${encodeURIComponent(platform + " " + searchQuery)}`;
  return `https://www.google.com/search?q=${encodeURIComponent(searchQuery + " online course " + platform)}`;
}

const PLATFORM_ICONS = {
  coursera: "🎓",
  udemy: "🎯",
  youtube: "▶️",
  edx: "🏫",
  "khan academy": "📚",
  "mit ocw": "🔬",
  "mit opencourseware": "🔬",
  bilkent: "🇹🇷",
  "itü": "🇹🇷",
  "odtü": "🇹🇷",
};

function platformIcon(name) {
  const key = (name || "").toLowerCase();
  for (const [k, v] of Object.entries(PLATFORM_ICONS)) {
    if (key.includes(k)) return v;
  }
  return "🌐";
}

function CourseCard({ course }) {
  return (
    <a
      href={buildSearchUrl(course.platform, course.search_query)}
      target="_blank"
      rel="noopener noreferrer"
      className="kp-course-card"
    >
      <div className="kp-course-top">
        <span className="kp-course-icon">{platformIcon(course.platform)}</span>
        <div className="kp-course-meta">
          <span className="kp-course-platform">{course.platform}</span>
          <div className="kp-course-badges">
            {course.level && <span className="kp-course-badge">{course.level}</span>}
            {course.language && <span className="kp-course-badge">{course.language}</span>}
            <span className={`kp-course-badge ${course.is_free ? "kp-badge-free" : "kp-badge-paid"}`}>
              {course.is_free ? "Ücretsiz" : "Ücretli"}
            </span>
          </div>
        </div>
      </div>
      <p className="kp-course-title">{course.title}</p>
      <p className="kp-course-desc">{course.description}</p>
    </a>
  );
}

function AICoursePanel({ outcome, courseName }) {
  const [state, setState] = useState("idle"); // idle | loading | done | error
  const [courses, setCourses] = useState([]);
  const [tip, setTip] = useState("");
  const [errMsg, setErrMsg] = useState("");

  const fetchRecommendations = useCallback(() => {
    setState("loading");
    setErrMsg("");
    fetch(`${API_BASE_URL}/api/ai/course-recommendations`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        outcome_code: outcome.code,
        outcome_description: outcome.description,
        outcome_type: outcome.type,
        course_name: courseName,
      }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (!data.success) throw new Error(data.error || "Bilinmeyen hata");
        setCourses(data.courses || []);
        setTip(data.tip || "");
        setState("done");
      })
      .catch((e) => {
        setErrMsg(e.message);
        setState("error");
      });
  }, [outcome, courseName]);

  if (state === "idle") {
    return (
      <button className="kp-ai-btn" onClick={fetchRecommendations}>
        🤖 AI Kurs Öner
      </button>
    );
  }

  if (state === "loading") {
    return <div className="kp-ai-loading">🤖 AI kurs önerileri hazırlanıyor…</div>;
  }

  if (state === "error") {
    return (
      <div className="kp-ai-error">
        Öneriler alınamadı: {errMsg}
        <button className="kp-ai-retry" onClick={fetchRecommendations}>Tekrar dene</button>
      </div>
    );
  }

  return (
    <div className="kp-ai-results">
      <div className="kp-ai-header">
        <span>🤖 AI Kurs Önerileri</span>
        <button className="kp-ai-refresh" onClick={fetchRecommendations} title="Yenile">↻</button>
      </div>
      {tip && <div className="kp-ai-tip">💡 {tip}</div>}
      <div className="kp-course-grid">
        {courses.map((c, i) => <CourseCard key={i} course={c} />)}
      </div>
    </div>
  );
}

function StudyPlanCard({ code, description, pct, type, courseName }) {
  const { text, cls } = priorityLabel(pct);
  const showPlan = pct === null || pct < 75;
  if (!showPlan) return null;

  let advice = "";
  if (pct === null || pct === undefined) {
    advice = "Bu çıktı için henüz değerlendirme yapılmamış. İlgili konuları gözden geçirin.";
  } else if (pct < 40) {
    advice = "Bu konuyu acil olarak tekrar edin. Ders notlarını baştan sona çalışın, ek kaynaklar kullanın ve hocaya danışın.";
  } else if (pct < 60) {
    advice = "Bu alanda eksikleriniz var. Konuyu tekrar gözden geçirin ve örnek sorular çözün.";
  } else {
    advice = "Genel düzeyiniz iyi, ancak bu çıktıyı pekiştirmek için pratik yapmaya devam edin.";
  }

  const outcome = { code, description, type };

  return (
    <div className={`kp-card kp-card-${cls.split("-").pop()}`}>
      <div className="kp-card-header">
        <span className="kp-code">{code}</span>
        <span className={`kp-badge ${cls}`}>{text}</span>
        <PctCell value={pct} />
      </div>
      <p className="kp-desc">{description}</p>
      <div className="kp-advice">
        <span className="kp-advice-icon">📋</span>
        <span>{advice}</span>
      </div>
      <div className="kp-card-footer">
        <span className="kp-type-tag">{type}</span>
        <AICoursePanel outcome={outcome} courseName={courseName} />
      </div>
    </div>
  );
}

function labelForExamType(t) {
  switch (t) {
    case "vize": return "Ara Sınav";
    case "final": return "Final";
    case "but":
    case "bütünleme": return "Bütünleme";
    case "quiz": return "Kısa Sınav";
    case "odev":
    case "ödev": return "Ödev";
    case "pretest": return "Ön Test";
    case "pro": return "Proje";
    default: return t.charAt(0).toUpperCase() + t.slice(1);
  }
}

export default function KisiselPlanlama() {
  const [courses, setCourses] = useState([]);
  const [courseId, setCourseId] = useState("");
  const [courseName, setCourseName] = useState("");
  const [classData, setClassData] = useState(null);
  const [studentId, setStudentId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const akademisyenId = sessionStorage.getItem("akademisyen_id");
    if (!akademisyenId) return;
    fetch(`${API_BASE_URL}/api/akademisyen/courses?akademisyen_id=${akademisyenId}`, {
      credentials: "include",
    })
      .then((r) => r.json())
      .then((data) => setCourses(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!courseId) {
      setClassData(null);
      setStudentId("");
      setCourseName("");
      return;
    }
    const selected = courses.find((c) => String(c.id) === String(courseId));
    setCourseName(selected ? `${selected.code} — ${selected.name}` : "");
    setLoading(true);
    setError("");
    fetch(
      `${API_BASE_URL}/api/pre-midterm/post-midterm-class-breakdown?course_id=${courseId}`,
      { credentials: "include" }
    )
      .then((r) => r.json())
      .then((data) => {
        if (data.error) throw new Error(data.error);
        setClassData(data);
        setStudentId("");
      })
      .catch((e) => setError(`Veriler alınamadı: ${e.message}`))
      .finally(() => setLoading(false));
  }, [courseId, courses]);

  const student = useMemo(() => {
    if (!classData || !studentId) return null;
    return classData.students?.find((s) => String(s.student_no) === studentId) || null;
  }, [classData, studentId]);

  const weakOutcomes = useMemo(() => {
    if (!student) return { oc: [], pc: [] };
    const oc = (student.learning_outcomes || []).filter((o) => (o.pct ?? -1) < 75);
    const pc = (student.program_outcomes || []).filter((o) => (o.pct ?? -1) < 75);
    return { oc, pc };
  }, [student]);

  const planCount = weakOutcomes.oc.length + weakOutcomes.pc.length;

  return (
    <div className="pm-wrap">
      <h2 className="pm-title">Kişisel Çalışma Planı</h2>

      <div className="pm-controls">
        <label>
          Ders:
          <select value={courseId} onChange={(e) => setCourseId(e.target.value)}>
            <option value="">— Ders seçin —</option>
            {courses.map((c) => (
              <option key={c.id} value={c.id}>
                {c.code} — {c.name}
              </option>
            ))}
          </select>
        </label>

        {classData && classData.students?.length > 0 && (
          <label>
            Öğrenci:
            <select value={studentId} onChange={(e) => setStudentId(e.target.value)}>
              <option value="">— Öğrenci seçin —</option>
              {classData.students.map((s) => (
                <option key={s.student_no} value={String(s.student_no)}>
                  {s.student_no} — {s.student_name || "İsimsiz"}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      {loading && <span className="pm-loading">Yükleniyor…</span>}
      {error && <div className="pm-error">{error}</div>}

      {student && (
        <>
          <section className="pm-section kp-summary-section">
            <h3>{student.student_name || student.student_no} — Genel Durum</h3>
            <div className="kp-summary-row">
              <div className="kp-summary-item">
                <span className="kp-summary-label">Genel Puan</span>
                <PctCell value={student.overall_post_midterm_pct} />
              </div>
              <div className="kp-summary-item">
                <span className="kp-summary-label">ÖÇ Sayısı</span>
                <span className="kp-summary-val">{student.learning_outcomes?.length || 0}</span>
              </div>
              <div className="kp-summary-item">
                <span className="kp-summary-label">PÖÇ Sayısı</span>
                <span className="kp-summary-val">{student.program_outcomes?.length || 0}</span>
              </div>
              <div className="kp-summary-item">
                <span className="kp-summary-label">Geliştirilmesi Gereken</span>
                <span className={`kp-summary-val ${planCount > 0 ? "kp-val-warn" : "kp-val-ok"}`}>
                  {planCount}
                </span>
              </div>
            </div>

            {student.exam_type_breakdown?.length > 0 && (
              <div className="kp-breakdown-row">
                {student.exam_type_breakdown.map((b) => (
                  <div key={b.exam_type} className="kp-breakdown-chip">
                    <span>{labelForExamType(b.exam_type)}</span>
                    <PctCell value={b.score_pct} />
                    <span className="kp-muted">%{Math.round((b.weight_norm || 0) * 100)} ağırlık</span>
                  </div>
                ))}
              </div>
            )}
          </section>

          {planCount === 0 ? (
            <section className="pm-section">
              <div className="kp-success-msg">
                🎉 Tüm öğrenme ve program çıktılarında yeterli performans gösteriyorsunuz (≥75). Çalışmalarınıza devam edin!
              </div>
            </section>
          ) : (
            <section className="pm-section">
              <h3>Kişisel Çalışma Planı ({planCount} konu)</h3>
              <p className="kp-plan-intro">
                Aşağıdaki çıktılar geliştirilmesi gereken alanları göstermektedir. "AI Kurs Öner" butonuna basarak her konu için çevrimiçi kurs önerileri alabilirsiniz.
              </p>

              {weakOutcomes.oc.length > 0 && (
                <>
                  <h4 className="kp-group-title">Öğrenme Çıktıları (ÖÇ)</h4>
                  <div className="kp-cards">
                    {weakOutcomes.oc.map((o) => (
                      <StudyPlanCard
                        key={o.id}
                        code={o.code}
                        description={o.description}
                        pct={o.pct}
                        type="ÖÇ"
                        courseName={courseName}
                      />
                    ))}
                  </div>
                </>
              )}

              {weakOutcomes.pc.length > 0 && (
                <>
                  <h4 className="kp-group-title">Program Öğrenme Çıktıları (PÖÇ)</h4>
                  <div className="kp-cards">
                    {weakOutcomes.pc.map((o) => (
                      <StudyPlanCard
                        key={o.id}
                        code={o.code}
                        description={o.description}
                        pct={o.pct}
                        type="PÖÇ"
                        courseName={courseName}
                      />
                    ))}
                  </div>
                </>
              )}
            </section>
          )}
        </>
      )}

      {!student && !loading && courseId && classData && (
        <div className="pm-section">
          <p style={{ color: "#6b7280", textAlign: "center" }}>Yukarıdan bir öğrenci seçin.</p>
        </div>
      )}
    </div>
  );
}
